import React, { useEffect, useRef, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { Quote, ArrowLeft, ArrowRight, Star } from "lucide-react";
import { useI18n } from "../../i18n";

const TESTIMONIALS_EN = [
  { name: "Sanne D.", city: "Amsterdam", text: "Quick PostNL delivery and beautiful packaging. iDEAL works perfectly — exactly what a Dutch shop should feel like.", avatar: "https://i.pravatar.cc/120?img=47", rating: 5 },
  { name: "Lars V.", city: "Rotterdam", text: "Beautiful objects with a clear point of view. The ceramic lamp lit up my whole flat — and the support team replies within an hour.", avatar: "https://i.pravatar.cc/120?img=33", rating: 5 },
  { name: "Iris M.", city: "Utrecht", text: "Klarna pay-later was effortless. My order arrived next day via PostNL and the linen cushion is even nicer in person.", avatar: "https://i.pravatar.cc/120?img=49", rating: 5 },
  { name: "Bram K.", city: "Eindhoven", text: "The cookware set arrived in two days. Free returns sealed the deal — but I'll be keeping these.", avatar: "https://i.pravatar.cc/120?img=12", rating: 5 },
  { name: "Femke J.", city: "The Hague", text: "Finally an EN/NL store that actually feels designed and not just translated. Bought twice already.", avatar: "https://i.pravatar.cc/120?img=44", rating: 5 },
];
const TESTIMONIALS_NL = [
  { name: "Sanne D.", city: "Amsterdam", text: "Snelle PostNL-bezorging en mooie verpakking. iDEAL werkt perfect — precies hoe een Nederlandse winkel hoort te voelen.", avatar: "https://i.pravatar.cc/120?img=47", rating: 5 },
  { name: "Lars V.", city: "Rotterdam", text: "Mooie objecten met een duidelijke smaak. De keramische lamp gaf mijn appartement een complete nieuwe sfeer. Topservice.", avatar: "https://i.pravatar.cc/120?img=33", rating: 5 },
  { name: "Iris M.", city: "Utrecht", text: "Klarna achteraf betalen was supermakkelijk. Mijn bestelling kwam de volgende dag via PostNL en het linnen kussen is nog mooier in het echt.", avatar: "https://i.pravatar.cc/120?img=49", rating: 5 },
  { name: "Bram K.", city: "Eindhoven", text: "De pannenset kwam binnen twee dagen aan. Gratis retour gaf de doorslag — maar deze houd ik!", avatar: "https://i.pravatar.cc/120?img=12", rating: 5 },
  { name: "Femke J.", city: "Den Haag", text: "Eindelijk een EN/NL winkel die echt ontworpen voelt en niet alleen vertaald. Al twee keer besteld.", avatar: "https://i.pravatar.cc/120?img=44", rating: 5 },
];

const TestimonialsCarousel = () => {
  const { lang, t } = useI18n();
  const data = lang === "nl" ? TESTIMONIALS_NL : TESTIMONIALS_EN;
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: "start" });
  const [selected, setSelected] = useState(0);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelected(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect);
    return () => emblaApi.off("select", onSelect);
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    intervalRef.current = setInterval(() => emblaApi.scrollNext(), 5500);
    return () => clearInterval(intervalRef.current);
  }, [emblaApi]);

  return (
    <section className="bg-secondary-warm py-20" data-testid="testimonials-section">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-5">
            <div className="font-label text-terracotta">{lang === "nl" ? "★★★★★ 4.9 van 5" : "★★★★★ 4.9 of 5"}</div>
            <h2 className="font-display text-4xl md:text-6xl tracking-tighter font-light mt-3 leading-[0.95]">{t("home_reviews")}</h2>
            <p className="text-ink-muted mt-4 max-w-sm">{lang === "nl" ? "Meer dan 12.000 tevreden klanten in Nederland." : "Over 12,000 happy customers across the Netherlands."}</p>
            <div className="mt-8 hidden md:flex items-center gap-3">
              <button onClick={() => emblaApi?.scrollPrev()} className="w-10 h-10 border border-ink flex items-center justify-center hover:bg-ink hover:text-white transition-colors" data-testid="testimonial-prev"><ArrowLeft className="w-4 h-4" /></button>
              <button onClick={() => emblaApi?.scrollNext()} className="w-10 h-10 border border-ink flex items-center justify-center hover:bg-ink hover:text-white transition-colors" data-testid="testimonial-next"><ArrowRight className="w-4 h-4" /></button>
            </div>
          </div>
          <div className="md:col-span-7 overflow-hidden" ref={emblaRef}>
            <div className="flex gap-4">
              {data.map((r, i) => (
                <div key={i} className="flex-[0_0_88%] md:flex-[0_0_60%] bg-white border border-black/5 p-7 min-w-0" data-testid={`testimonial-${i}`}>
                  <Quote className="w-7 h-7 text-terracotta mb-4" />
                  <div className="flex gap-0.5 mb-3">
                    {Array.from({ length: r.rating }).map((_, j) => <Star key={j} className="w-3.5 h-3.5 fill-terracotta text-terracotta" />)}
                  </div>
                  <p className="text-ink leading-relaxed text-[15px]">{r.text}</p>
                  <div className="mt-5 flex items-center gap-3 pt-4 border-t border-black/5">
                    <img src={r.avatar} alt="" className="w-10 h-10 rounded-full object-cover grayscale" />
                    <div>
                      <div className="font-medium text-sm">{r.name}</div>
                      <div className="text-xs text-ink-muted font-label">{r.city}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex gap-1.5 mt-6 md:hidden">
              {data.map((_, i) => (
                <button key={i} onClick={() => emblaApi?.scrollTo(i)} className={`h-1 transition-all ${i === selected ? "w-8 bg-ink" : "w-4 bg-ink/20"}`} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsCarousel;
